import { Box, Container, Flex, type FlexProps } from '@chakra-ui/react'
import type { ReactNode } from 'react'

import UserQ from '@/queries/UserQ'
import { useAppContext } from '@/client/context/app'
import { useUser } from '@/client/context/user'
import PageNotFoundError from '@/client/error/PageNotFoundError'
import { getUserUrl } from '@/client/services/url'

import Layout from '@/components/Layout/Layout'
import PlatformHeader from '@/components/Layout/PlatformHeader'
import { useUserFromQuery } from '@/components/Profile/helpers'
import Tabs from '@/components/Tabs/Tabs'

import ProfileHeader from './ProfileHeader'
import type { UserDetailTab } from './types'

export default function ProfileLayout({
  children,
  currentTab,
  ...props
}: {
  children: ReactNode
  currentTab: UserDetailTab
} & FlexProps): JSX.Element {
  const renderContext = useAppContext()
  const currentUser = useUser()

  const { user } = useUserFromQuery()

  const userId = user(UserQ.id) ?? 0
  const username = user(UserQ.username)

  const hasBeenSuspended = user(UserQ.hasBeenSuspended()) ?? false
  const displayName = user(UserQ.display_name)
  const userIsTutor: boolean = user(UserQ.hasTutorRole()) ?? true

  const TAB_TO_URL = {
    Overview: getUserUrl({ userId, username }),
    Achievements: getUserUrl({ userId, username, path: 'achievements' }),
    Sessions: getUserUrl({ userId, username, path: 'sessions' }),
    Feedback: getUserUrl({ userId, username, path: 'feedback' }),
  }

  const tabs: UserDetailTab[] = userIsTutor
    ? ['Overview', 'Achievements', 'Sessions', 'Feedback']
    : ['Overview', 'Achievements']

  if (!renderContext.dryRun && (hasBeenSuspended || displayName == null)) {
    throw new PageNotFoundError('Oh no! This user does not exist.')
  }

  return (
    <Layout
      title={displayName ?? ''}
      meta={{
        title: displayName == null ? null : `${displayName.trim()}'s Schoolhouse Profile`,
        description: `Schoolhouse.world offers free online tutoring in math, SAT prep, and more—all run by certified volunteer tutors. See what ${displayName} is up to on Schoolhouse!`,
      }}
      header={
        <PlatformHeader
          heading={{ title: userId === currentUser.id ? 'My Profile' : `${displayName}'s Profile` }}
        />
      }
      canonical={getUserUrl({ userId: userId ?? 0, username })}
    >
      <Flex direction="column" height="100%" flex={1} {...props}>
        <Box>
          <ProfileHeader user={user} />
          <Container pt="0px !important" pb="0px !important">
            <Tabs<UserDetailTab>
              currentTab={currentTab}
              tabToUrl={t => TAB_TO_URL[t]}
              tabs={[...tabs]}
            />
          </Container>
        </Box>
        <Box bg="bg.subtle" flex={1}>
          <Container>{children}</Container>
        </Box>
      </Flex>
    </Layout>
  )
}
